import React, { useEffect, useRef, useState } from 'react';
import './SectionBackground.css';

/**
 * Shared section wrapper that renders a background image with scroll-triggered
 * fade-in. Content stays visible at all times — only the decorative photo fades.
 *
 * Earlier versions set wrapper opacity to 0 until IntersectionObserver fired.
 * That reserved full ChartLayout height while invisible, which produced long
 * white gaps (especially after hash jumps past mid-page sections).
 */

/* ── Hook: manages IntersectionObserver for a set of section IDs ────── */

export function useSectionReveal(
  sectionIds: string[],
  /** gate – observer is only created when `ready` is true (prevents firing before data loads) */
  ready: boolean = true
) {
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!ready) return;

    const markVisible = (id: string) => {
      setVisible((prev) => {
        if (prev[id]) return prev;
        // Also reveal every earlier section in DOM/sectionIds order so a mid-page
        // reveal (or hash scroll) never leaves tall invisible gaps above.
        const next = { ...prev, [id]: true };
        const idx = sectionIds.indexOf(id);
        if (idx > 0) {
          for (let i = 0; i < idx; i += 1) {
            next[sectionIds[i]] = true;
          }
        }
        return next;
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-section-id');
            if (id) {
              markVisible(id);
              observer.unobserve(entry.target);
            }
          }
        });
      },
      // Generous rootMargin so sections reveal before they dominate the viewport
      { threshold: 0.01, rootMargin: '120px 0px 80px 0px' }
    );

    const timer = setTimeout(() => {
      sectionIds.forEach((id) => {
        const el = refs.current[id];
        if (!el) return;
        // Eagerly reveal anything already on / near screen (first paint, restored scroll, hash).
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 0;
        if (rect.bottom > -40 && rect.top < vh + 120) {
          markVisible(id);
          return;
        }
        observer.observe(el);
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, ...sectionIds]);

  return { visible, refs };
}

/* ── Component: wraps a single section with background + animation ──── */

interface SectionWrapperProps {
  sectionId: string;
  bgMap: Record<string, string>;
  refs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  visible: Record<string, boolean>;
  getSectionStyle: (id: string) => React.CSSProperties;
  children: React.ReactNode;
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({
  sectionId,
  bgMap,
  refs,
  visible,
  getSectionStyle,
  children,
}) => (
  <div
    className="section-bg-wrapper"
    data-section-id={sectionId}
    ref={(el) => { refs.current[sectionId] = el; }}
    style={{
      ...getSectionStyle(sectionId),
      // Content must stay opaque — opacity:0 previously caused long white gaps.
      opacity: 1,
    }}
  >
    {bgMap[sectionId] && (
      <img
        src={bgMap[sectionId]}
        alt=""
        aria-hidden="true"
        className="section-bg-img"
        loading="lazy"
        decoding="async"
        style={{ opacity: visible[sectionId] ? 0.18 : 0 }}
      />
    )}
    {children}
  </div>
);

export default SectionWrapper;
